import ECharts from "@components/common/chart";
import { BarSeriesOption, EChartsOption, EChartsType } from "echarts";
import { HVIcon, HVTable } from "@repo/HVComponents";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { mainChart } from "@components/widgets/stopped-vehicle-statistics/widget/Chart";
import { useLang } from "@hooks/useLang";
import {
  ANALYTICS_CHART_VIEW_TYPE,
  VEHICLE_ICON_MAP
} from "@resources/Constants";
import IHVTable from "@repo/HVComponents/dist/3.409.1/types/components/HVTable/IHVTable";
import { ChartCardProps } from "../Interfaces";
import * as S from "./StoppedVehicle.styled";
import { getColorByVehicleType, prepareColumnObject } from "../../chartUtils";
import {
  CombinedSummery,
  SingleSummery,
  StoppedVehicleChartData
} from "./StoppedVehicleInterfaces";

const StoppedVehicleStatus = (props: ChartCardProps) => {
  const theme = useTheme();
  const { translate } = useLang();
  const vehicleTypes = ANALYTICS_CHART_VIEW_TYPE.VEHICLE.slice(1);

  const { processedSummary, combinedSummery } = useMemo(() => {
    if (!props.analyticsData?.length) return {};

    const combinedSummery: CombinedSummery = {};

    const summery = props.analyticsData.map((entry) => {
      const total = entry.total || 0;
      const row: SingleSummery = {};

      const chartData = entry.originalStoppedVehicleData as
        | StoppedVehicleChartData
        | undefined;

      if (chartData) {
        vehicleTypes.forEach((type) => {
          const values = chartData[type];
          const count = values[values.length - 1];
          const percentage =
            total && total > 0 ? ((count / total) * 100).toFixed(1) : 0;
          row[type] = {
            value: `${count.toLocaleString()} (${percentage}%)`,
            count,
            color: getColorByVehicleType(theme, type),
            locationName: entry.locationName || "Unknown Location"
          };
          if (combinedSummery[type] === undefined) {
            combinedSummery[type] = {
              ...row[type],
              total: total || 0,
              percentage: `(${percentage}%)`
            };
          } else {
            combinedSummery[type].total += total;
            const combinedTotal = combinedSummery[type].total;
            combinedSummery[type].count += row[type].count;
            combinedSummery[type].percentage = `(${
              combinedTotal > 0
                ? ((combinedSummery[type].count / combinedTotal) * 100).toFixed(
                    1
                  )
                : 0
            }%)`;
            combinedSummery[type].color = getColorByVehicleType(theme, type);
            combinedSummery[type].locationName =
              entry.locationName || "Unknown Location";
          }
        });
      }

      return row;
    });

    return { processedSummary: summery, combinedSummery };
  }, [props.analyticsData]);

  const { option, highest, searchTotal } = useMemo(() => {
    const sum: { [x: string]: number[] } = {};

    vehicleTypes.forEach((type) => {
      sum[type] = [];
    });

    props.analyticsData.forEach((item) => {
      if (!item.originalStoppedVehicleData) return;

      vehicleTypes.forEach((type) => {
        const values = (
          item.originalStoppedVehicleData as StoppedVehicleChartData
        )[type];
        if (Array.isArray(values)) {
          if (sum[type].length === 0) {
            sum[type] = [...values];
          } else {
            sum[type] = sum[type].map((v, idx) => v + (values[idx] || 0));
          }
        }
      });
    });

    const searchTotal = props.analyticsData.reduce((acc, entry) => {
      if (entry.total) {
        return acc + entry.total;
      }
      return acc;
    }, 0);

    const highest = Object.keys(sum).reduce(
      (max, key) => {
        const value = sum[key][sum[key].length - 1] || 0;
        return value > max.value ? { value, key } : max;
      },
      { value: 0, key: "" }
    );
    const chartOptions = mainChart(
      theme,
      "BAR_CATEGORY_STACK",
      props.timeInterval,
      sum,
      searchTotal,
      true
    );

    return { option: chartOptions as EChartsOption, highest, searchTotal };
  }, [props.analyticsData, theme]);

  const renderInsightStatement = () => {
    let translation = translate("stopped_vehicle_by_type_insight", {
      searchTotal: `<b>${Number(searchTotal?.toFixed(0)).toLocaleString()}</b>`,
      highestKey: `<b>${
        highest.key.charAt(0).toUpperCase() + highest.key.slice(1)
      }</b>`,
      highestValue: `<b>${Number(
        highest.value?.toFixed(0)
      ).toLocaleString()}</b>`
    });
    if (highest.key === "" || highest.value === 0) {
      translation = translate("no_data_this_period");
    }

    return (
      <S.Text secondary dangerouslySetInnerHTML={{ __html: translation }} />
    );
  };

  const resetBorderRadius = (e?: EChartsType) => {
    if (!combinedSummery || !e) return;

    const keys = Object.keys(combinedSummery);

    const checkIsLastNonZero = (index: number) => {
      const keysAfterCurrent = keys.slice(index + 1);
      return keysAfterCurrent.every(
        (item) => combinedSummery[item].percentage === "(0%)"
      );
    };

    const currentOption = e.getOption();
    if (!currentOption) return;
    const currentSeries = currentOption.series as BarSeriesOption[];

    const updatedSeries =
      currentSeries &&
      currentSeries.map((seriesItem, index) => {
        let borderRadius: [number, number, number, number] = [0, 0, 0, 0];

        if (checkIsLastNonZero(index) || index === keys.length - 1) {
          borderRadius = [0, 4, 4, 0];
        } else if (index === 0) {
          borderRadius = [4, 0, 0, 4];
        }

        return {
          ...seriesItem,
          itemStyle: {
            ...(seriesItem.itemStyle || {}),
            borderRadius
          }
        };
      });

    e.setOption(
      {
        xAxis: option.xAxis,
        yAxis: option.yAxis,
        grid: option.grid,
        tooltip: option.tooltip,
        series: updatedSeries
      },
      { notMerge: true }
    );
  };

  const getStoppedVehicleChartTableColumn = (): IHVTable["columns"] => {
    return [
      {
        header: translate("location"),
        accessorKey: "car.locationName",
        size: 50,
        mantineTableHeadCellProps: {
          style: { textAlign: "left" }
        },
        mantineTableBodyCellProps: {
          style: { textAlign: "left" }
        }
      },
      prepareColumnObject(translate("car"), "car", 50),
      prepareColumnObject(translate("bus"), "bus", 50),
      prepareColumnObject(translate("truck"), "truck", 50),
      prepareColumnObject(translate("motorcycle"), "motorcycle", 50),
      prepareColumnObject(translate("bicycle"), "bicycle", 50)
    ];
  };

  return (
    <S.StatusChartWrapper>
      <S.TitleContainer>
        <S.Title>{translate("stopped_vehicle_count_by_vehicle_type")}</S.Title>
        <S.InsightStatement>{renderInsightStatement()}</S.InsightStatement>
      </S.TitleContainer>
      <S.CountContainer>
        <S.Label>{translate("total_count")}</S.Label>
        <S.Count>{Number(searchTotal).toLocaleString()}</S.Count>
      </S.CountContainer>
      <S.chartContainer>
        <ECharts
          option={option as EChartsOption}
          onLoad={resetBorderRadius}
          style={{
            height: "20px"
          }}
          mainChartType={`${props.type}_vehicle_type`}
        />
        <S.LegendContainer>
          {combinedSummery &&
            Object.keys(combinedSummery).map((key) => {
              return (
                <S.LegendItem key={key}>
                  <S.ColorBox color={combinedSummery[key].color} />
                  <HVIcon name={VEHICLE_ICON_MAP[key]} width={20} height={20} />
                  <S.LegendLabel className="label">
                    {translate(key)}
                  </S.LegendLabel>
                  <S.LegendCount className="count">
                    {combinedSummery[key].count.toLocaleString()}
                    <span className="percentage">
                      {combinedSummery[key].percentage}
                    </span>
                  </S.LegendCount>
                </S.LegendItem>
              );
            })}
        </S.LegendContainer>
        <S.ChartTable>
          <HVTable
            columns={getStoppedVehicleChartTableColumn()}
            hideSelectionColumn
            data={processedSummary || []}
            selectedRowId=""
            tableOptions={{
              enableSorting: true,
              mantineTableBodyRowProps: {
                style: {
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "18px",
                  height: "40px",
                  minHeight: "40px",
                  color: "var(--hv-text-normal-primary)"
                }
              },
              mantineTableHeadRowProps: {
                style: {
                  height: "32px",
                  minHeight: "32px",
                  color: "var(--hv-text-normal-secondary)"
                }
              },
              mantinePaperProps: {
                style: {
                  overflowY: "auto",
                  height: "fit-content"
                }
              }
            }}
          />
        </S.ChartTable>
      </S.chartContainer>
    </S.StatusChartWrapper>
  );
};

export default StoppedVehicleStatus;
